import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import { useDashboard } from '../../../contexts/DashboardContext';
import { postAppSkillChat } from '../../../api/extensions.api';

const MAX_MESSAGE = 4000;
const HISTORY_TURNS = 10;

const markdownComponents = {
  p: ({ children }) => <Typography variant="body2" sx={{ mb: 1, '&:last-child': { mb: 0 } }}>{children}</Typography>,
  ul: ({ children }) => <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>{children}</Box>,
  li: ({ children }) => <Box component="li" sx={{ mb: 0.25 }}><Typography variant="body2" component="span">{children}</Typography></Box>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
};

function Bubble({ from, text, toolCalls }) {
  const mine = from === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <Paper
        elevation={0}
        data-testid={mine ? 'skill-chat-user' : 'skill-chat-answer'}
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: '85%',
          borderRadius: 2,
          bgcolor: mine ? 'primary.main' : 'action.hover',
          color: mine ? 'primary.contrastText' : 'text.primary',
          overflowWrap: 'anywhere',
        }}
      >
        {mine
          ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
          : <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>}
        {!mine && toolCalls > 0 && (
          <Chip size="small" variant="outlined" sx={{ mt: 0.5 }} label={`Used ${toolCalls} tool${toolCalls === 1 ? '' : 's'}`} />
        )}
      </Paper>
    </Box>
  );
}

export default function DashboardSkillChat({ panelId, config }) {
  const { config: appConfig } = useDashboard();
  const appId = appConfig?._id || appConfig?.id;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, sending]);

  if (!config?.skillId) {
    return <Alert severity="info" sx={{ m: 1 }}>Choose a skill for this panel in App Builder.</Alert>;
  }

  const send = async () => {
    const message = draft.trim().slice(0, MAX_MESSAGE);
    if (!message || sending) return;
    if (!appId) {
      setError('Save the app before chatting with its skill.');
      return;
    }
    const history = messages.slice(-HISTORY_TURNS * 2).map(({ role, text }) => ({ role, text }));
    setMessages((prev) => [...prev, { role: 'user', text: message }]);
    setDraft('');
    setError(null);
    setSending(true);
    const { answer, toolCalls, error: err } = await postAppSkillChat(appId, { panelId, message, history });
    setSending(false);
    if (err) {
      setError(err);
      return;
    }
    setMessages((prev) => [...prev, { role: 'assistant', text: answer || '(no answer)', toolCalls: toolCalls.length }]);
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 260,
    }}
    >
      <Box sx={{
        flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
      }}
      >
        {config.greeting && <Bubble from="assistant" text={config.greeting} />}
        {!config.greeting && messages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            Ask {config.skillName || 'the skill'} anything.
          </Typography>
        )}
        {messages.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Bubble key={i} from={m.role} text={m.text} toolCalls={m.toolCalls} />
        ))}
        {sending && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary',
          }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption">Thinking…</Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }} onClose={() => setError(null)} data-testid="skill-chat-error">{error}</Alert>}
      <Box
        component="form"
        onSubmit={(e) => { e.preventDefault(); send(); }}
        sx={{
          display: 'flex', gap: 1, p: 1, borderTop: 1, borderColor: 'divider',
        }}
      >
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          inputProps={{ 'data-testid': 'skill-chat-input', 'aria-label': 'Message' }}
        />
        <IconButton type="submit" color="primary" disabled={sending || !draft.trim()} aria-label="Send" data-testid="skill-chat-send">
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
